import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Paper, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Autocomplete, CircularProgress, Grow, Tabs, Tab, Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FlagIcon from '@mui/icons-material/Flag';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import DevicesIcon from '@mui/icons-material/Devices';
import Loading from '../../components/Loading';

function SoftwareLicenses() {
    const [licenses, setLicenses] = useState([]);
    const [users, setUsers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [filter, setFilter] = useState('All');
    const [tabValue, setTabValue] = useState(0);

    const [newLicense, setNewLicense] = useState({
        name: '', description: '', requested_by_id: '', requested_by_name: '', requested_by_email: '', department_id: '', country_id: '', supplier_id: '', status: 'Active'
    });
    const [search, setSearch] = useState('');
    
    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchLicenses();
        fetchSetupData();
    }, []);

    const fetchSetupData = async () => {
        try {
            const [resUsers, resDivs, resCountries, resSuppliers] = await Promise.all([
                axios.get(`${API_URL}/setup/users`, { headers }),
                axios.get(`${API_URL}/setup/divisions`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers }),
                axios.get(`${API_URL}/it-suppliers`, { headers })
            ]);
            setUsers(resUsers.data);
            setDivisions(resDivs.data);
            setCountries(resCountries.data);
            setSuppliers(resSuppliers.data);
        } catch (e) { console.error(e); }
    };

    const fetchLicenses = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses`, { headers });
            setLicenses(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API_URL}/software-licenses`, newLicense, { headers });
            setOpen(false);
            setNewLicense({ name: '', description: '', requested_by_id: '', requested_by_name: '', requested_by_email: '', department_id: '', country_id: '', supplier_id: '', status: 'Active' });
            fetchLicenses();
        } catch (e) { console.error(e); }
    };

    const filtered = licenses.filter(l =>
        (filter === 'All' || l.status === filter) &&
        (l.name.toLowerCase().includes(search.toLowerCase()) ||
            (l.description && l.description.toLowerCase().includes(search.toLowerCase())))
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return '#4caf50'; // success
            case 'Expired': return '#f44336'; // error
            case 'Pending Renewal': return '#ff9800'; // warning
            default: return '#9e9e9e'; // default
        }
    };

    if (loading) return <Loading />;

    const summary = {
        total: licenses.length,
        active: licenses.filter(l => l.status === 'Active').length,
        expired: licenses.filter(l => l.status === 'Expired').length,
        pending: licenses.filter(l => l.status === 'Pending Renewal').length
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <DevicesIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            Software Licenses
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Manage organization software assets, renewals, and subscriptions
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

            <Grow in={true} timeout={800}>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'transform 0.2s', border: '1px solid rgba(0,0,0,0.04)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.06)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 48, height: 48, mb: 1.5, boxShadow: `0 4px 10px #e3f2fd` }}>
                                    <DevicesIcon />
                                </Avatar>
                                <Typography variant="h5" color="#1976d2" fontWeight="800">{summary.total}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Total Licenses</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'transform 0.2s', border: '1px solid rgba(0,0,0,0.04)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.06)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: '#e8f5e9', color: '#4caf50', width: 48, height: 48, mb: 1.5, boxShadow: `0 4px 10px #e8f5e9` }}>
                                    <DevicesIcon />
                                </Avatar>
                                <Typography variant="h5" color="#4caf50" fontWeight="800">{summary.active}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Active Licenses</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                         <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'transform 0.2s', border: '1px solid rgba(0,0,0,0.04)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.06)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: '#fff3e0', color: '#ff9800', width: 48, height: 48, mb: 1.5, boxShadow: `0 4px 10px #fff3e0` }}>
                                    <HistoryIcon />
                                </Avatar>
                                <Typography variant="h5" color="#ff9800" fontWeight="800">{summary.pending}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Pending Renewal</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                         <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'transform 0.2s', border: '1px solid rgba(0,0,0,0.04)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.06)' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: '#ffebee', color: '#f44336', width: 48, height: 48, mb: 1.5, boxShadow: `0 4px 10px #ffebee` }}>
                                    <DevicesIcon />
                                </Avatar>
                                <Typography variant="h5" color="#f44336" fontWeight="800">{summary.expired}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Expired</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grow>

            <Grow in={true} timeout={1000}>
                <Paper elevation={0} sx={{ borderRadius: 4, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 4, overflow: 'hidden', bgcolor: 'white' }}>
                    <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 1, flexWrap: 'wrap', gap: 2 }}>
                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 60, fontSize: '0.95rem' }, '& .Mui-selected': { color: 'primary.main' } }}>
                            <Tab label="Explore Licenses" />
                        </Tabs>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ borderRadius: 2, px: 3, py: 1.2, boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', textTransform: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', mb: 1 }}>
                            New License
                        </Button>
                    </Box>

                    <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'stretch', md: 'center' } }}>
                            <TextField size="medium" placeholder="Search licenses by name..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <SearchIcon color="primary" sx={{ mr: 1.5 }} /> }} sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.02)', '& fieldset': { borderColor: 'transparent' }, '&:hover fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: 'primary.main' } } }} />
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <FormControl size="medium" sx={{ minWidth: 200 }}>
                                    <Select value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ borderRadius: 3, bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.02)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}>
                                        <MenuItem value="All"><em>All Statuses</em></MenuItem>
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                                        <MenuItem value="Expired">Expired</MenuItem>
                                    </Select>
                                </FormControl>

                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                                    size="medium"
                                    sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}
                                >
                                    <ToggleButton value="grid" aria-label="grid view" sx={{ border: 'none', '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}}>
                                        <ViewModuleIcon />
                                    </ToggleButton>
                                    <ToggleButton value="list" aria-label="list view" sx={{ border: 'none', '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}}>
                                        <ViewListIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Grow>

            <Grow in={true} timeout={1200}>
            <Box>
                {viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filtered.map(l => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={l.id}>
                                <Card sx={{ height: '100%', borderRadius: 4, transition: 'all 0.2s', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' }, cursor: 'pointer' }} onClick={() => navigate(`/software-licenses/${l.id}`)}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: l.status === 'Active' ? '#e8f5e9' : l.status === 'Pending Renewal' ? '#fff3e0' : l.status === 'Expired' ? '#ffebee' : '#f0f0f0', color: getStatusColor(l.status), width: 44, height: 44, fontWeight: 'bold' }}>
                                                    {l.name ? l.name.charAt(0) : 'L'}
                                                </Avatar>
                                                <Box sx={{ overflow: 'hidden' }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" noWrap title={l.name}>{l.name}</Typography>
                                                    <Typography variant="body2" color="text.secondary" noWrap title={l.supplier_name || 'No Supplier'}>{l.supplier_name || 'No Supplier'}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Chip label={l.status} size="small" sx={{ fontWeight: 'bold', bgcolor: `${getStatusColor(l.status)}20`, color: getStatusColor(l.status), border: `1px solid ${getStatusColor(l.status)}50` }} />
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {l.latest_renewal ? (
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'bold' }}>Latest Renewal</Typography>
                                                        <Typography variant="body2" fontWeight="500">{new Date(l.latest_renewal).toLocaleDateString()}</Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                 <Box display="flex" alignItems="center" gap={1.5}>
                                                    <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'bold' }}>Latest Renewal</Typography>
                                                        <Typography variant="body2" fontWeight="500">-</Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'bold' }}>Department</Typography>
                                                    <Typography variant="body2" fontWeight="500" noWrap>{l.department_name || '-'}</Typography>
                                                </Box>
                                            </Box>

                                             {l.country_name && (
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <FlagIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 'bold' }}>Country</Typography>
                                                        <Typography variant="body2" fontWeight="500" noWrap>{l.country_name}</Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                    <Box sx={{ borderTop: '1px solid #f0f0f0', p: 1.5, display: 'flex', justifyContent: 'center', bgcolor: '#f8fafc', color: 'primary.main', fontWeight: 'bold', fontSize: '0.875rem' }}>
                                        View Details <ArrowForwardIcon sx={{ ml: 1, fontSize: 18 }} />
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
                        <TableContainer>
                            <Table sx={{ minWidth: 800, '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Latest Renewal</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Period</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Supplier</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Department</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Country</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Requested By</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Status</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.map((l) => (
                                        <TableRow key={l.id} hover onClick={() => navigate(`/software-licenses/${l.id}`)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fbff !important' } }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${getStatusColor(l.status)}15`, color: getStatusColor(l.status), fontSize: '1rem', fontWeight: 'bold' }}>{l.name.charAt(0)}</Avatar>
                                                    {l.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{l.latest_renewal ? new Date(l.latest_renewal).toLocaleDateString() : '-'}</TableCell>
                                            <TableCell>
                                                {(l.latest_start_date || l.latest_end_date) ? (
                                                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                                        {l.latest_start_date ? new Date(l.latest_start_date).toLocaleDateString() : '?'} - {l.latest_end_date ? new Date(l.latest_end_date).toLocaleDateString() : '?'}
                                                    </Typography>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>{l.supplier_name || '-'}</TableCell>
                                            <TableCell>{l.department_name || '-'}</TableCell>
                                            <TableCell>{l.country_name || '-'}</TableCell>
                                            <TableCell>{l.requested_by_name || '-'}</TableCell>
                                            <TableCell>
                                                <Chip label={l.status} size="small" sx={{ fontWeight: 'bold', bgcolor: `${getStatusColor(l.status)}20`, color: getStatusColor(l.status), border: `1px solid ${getStatusColor(l.status)}50` }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box onClick={(e) => { e.stopPropagation(); navigate(`/software-licenses/${l.id}`); }} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eff6ff', color: '#2563eb', borderRadius: 3, p: 1.2, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: '#2563eb', color: 'white', transform: 'scale(1.1)', boxShadow: '0 4px 10px rgba(37,99,235,0.3)' } }}>
                                                    <ArrowForwardIcon fontSize="small" />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                <DevicesIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 1, display: 'block', mx: 'auto' }} />
                                                No licenses found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Box>
            </Grow>

            {/* Create Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    New Software License
                    <CloseIcon onClick={() => setOpen(false)} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', mb: 2 }}>License Details</Typography>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="App Name"
                                fullWidth
                                variant="outlined"
                                value={newLicense.name}
                                onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                            />
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                value={newLicense.description}
                                onChange={(e) => setNewLicense({ ...newLicense, description: e.target.value })}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={adUsers}
                                        loading={adLoading}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option.displayName || option.name || '';
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            handleADSearch(newInputValue);
                                        }}
                                        filterOptions={(x) => x} 
                                        value={newLicense.requested_by_name ? { displayName: newLicense.requested_by_name, mail: newLicense.requested_by_email } : null}
                                        onChange={(e, newValue) => {
                                            if (typeof newValue === 'string') {
                                                setNewLicense({ 
                                                    ...newLicense, 
                                                    requested_by_name: newValue,
                                                    requested_by_email: '',
                                                    requested_by_id: null
                                                });
                                            } else if (newValue) {
                                                const name = newValue.displayName || newValue.username;
                                                const email = newValue.mail || newValue.email || newValue.userPrincipalName;
                                                
                                                setNewLicense({ 
                                                    ...newLicense, 
                                                    requested_by_name: name,
                                                    requested_by_email: email,
                                                    requested_by_id: null
                                                });
                                            } else {
                                                setNewLicense({ ...newLicense, requested_by_name: '', requested_by_email: '', requested_by_id: null });
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label="Requested By" 
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box>
                                                    <Typography variant="body1">{option.displayName || option.name}</Typography>
                                                    {(option.mail || option.userPrincipalName) && <Typography variant="caption" color="textSecondary">{option.mail || option.userPrincipalName}</Typography>}
                                                </Box>
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={divisions}
                                        getOptionLabel={(option) => option.name}
                                        value={divisions.find(d => d.id === newLicense.department_id) || null}
                                        onChange={(e, newValue) => setNewLicense({ ...newLicense, department_id: newValue ? newValue.id : null })}
                                        renderInput={(params) => <TextField {...params} label="Department" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={countries}
                                        getOptionLabel={(option) => option.name}
                                        value={countries.find(c => c.id === newLicense.country_id) || null}
                                        onChange={(e, newValue) => setNewLicense({ ...newLicense, country_id: newValue ? newValue.id : null })}
                                        renderInput={(params) => <TextField {...params} label="Country" />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={suppliers}
                                        getOptionLabel={(option) => option.name}
                                        value={suppliers.find(s => s.id === newLicense.supplier_id) || null}
                                        onChange={(e, newValue) => setNewLicense({ ...newLicense, supplier_id: newValue ? newValue.id : null })}
                                        renderInput={(params) => <TextField {...params} label="Supplier" />}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={newLicense.status}
                                            label="Status"
                                            onChange={(e) => setNewLicense({ ...newLicense, status: e.target.value })}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Expired">Expired</MenuItem>
                                            <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={!newLicense.name} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', px: 3 }}>Create License</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SoftwareLicenses;
